import { EmptyState, LazyLoader, UndoIcon } from '@bp/ui-components';
import { BpCard } from 'components/BpCard/BpCard';
import { FileListDownloader } from 'components/Files/components/FileList/FileListDownloader';
import { useSubmissions } from 'components/Submissions/hooks/useSubmissions';
import { TipTapTextExtender } from 'components/TipTap/TipTapTextExtender';
import { useRequireParams } from 'hooks/useRequireParams';
import { useTranslation } from 'react-i18next';
import styles from './PublicSubmissionCard.module.scss';

type PublicSubmissionCardProps = {
  submissionUuid: string;
  loading?: boolean;
  canRevoke?: boolean;
  onRevokePublicised?: (submissionUuid: string) => void;
};

export const PublicSubmissionCard = ({
  submissionUuid,
  loading,
  canRevoke,
  onRevokePublicised,
}: PublicSubmissionCardProps) => {
  const { t } = useTranslation();
  const { courseUuid, assignmentUuid } = useRequireParams(['courseUuid', 'assignmentUuid']);

  const { submissions } = useSubmissions({ courseUuid, assignmentUuid, submissionUuid });

  const submission = submissions.find((s) => s.uuid === submissionUuid && s.public);
  if (!submission) throw new Error('Submission not found');

  const material = submission.material;

  return (
    <BpCard
      className={styles['public-submission-card-teacher']}
      noPadding
      alwaysCollapsible
      header={{
        headline: t('submissions.publicFrom', { name: submission.owner.name }),
        actions:
          canRevoke && onRevokePublicised
            ? [
                {
                  callback: () => onRevokePublicised(submissionUuid),
                  text: t('submissions.revokePublicised'),
                  icon: <UndoIcon />,
                  hierarchy: 'tertiary',
                },
              ]
            : undefined,
      }}
    >
      {loading && <LazyLoader className={styles.loader} />}

      {!material.text ? (
        <EmptyState hideIcon subtitle={t('submissions.noAnswerText')} padding='l' />
      ) : (
        <TipTapTextExtender
          uuid={material.uuid}
          content={material.text}
          padding={material.fileEntries.length === 0 ? 'none' : 'default'}
        />
      )}
      {material.fileEntries && material.fileEntries.length > 0 && (
        <FileListDownloader files={material.fileEntries} className='mt-7' />
      )}
    </BpCard>
  );
};
